import { signInWithCustomToken } from "firebase/auth";
import { LoaderCircle } from "lucide-react";
import { useEffect, useState, type FC } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { auth } from "@/api/firebaseApi";
import { AuthHeader } from "@/features/login";
import { queryClient } from "@/lib/query/queryClient";

import { ClientPortalAPI, portalErrorCode } from "../api";
import { endClientPortalPreview } from "../preview";
import { takePortalReturn } from "../routes";
import { usePortalT } from "../i18n";
import { PublicPortalFrame, SignInLinkCard } from "./PortalSignInPage";

const REASONS = ["expired", "used", "invalid", "revoked", "off"] as const;
type Reason = (typeof REASONS)[number];

/** Links are single-use, so a remount (StrictMode, a re-render) must reuse
 * the first attempt instead of spending the token twice. */
const attempts = new Map<string, Promise<string>>();

const signInWith = (token: string): Promise<string> => {
  const existing = attempts.get(token);
  if (existing) return existing;
  const attempt = ClientPortalAPI.redeem(token).then(async (r) => {
    endClientPortalPreview();
    queryClient.clear();
    await signInWithCustomToken(auth, r.custom_token);
    localStorage.setItem("loginType", "client");
    localStorage.setItem("userId", r.foreign_national_id);
    return r.foreign_national_id;
  });
  attempts.set(token, attempt);
  return attempt;
};

/** Where an emailed invite or sign-in link lands. */
export const PortalWelcomePage: FC = () => {
  const { t } = usePortalT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [reason, setReason] = useState<Reason | null>(token ? null : "invalid");

  useEffect(() => {
    if (!token) return;
    signInWith(token)
      .then(() => navigate(takePortalReturn(), { replace: true }))
      .catch((error: unknown) => {
        const code = portalErrorCode(error);
        setReason(REASONS.find((r) => r === code) ?? "invalid");
      });
  }, [token, navigate]);

  if (!reason) {
    return (
      <PublicPortalFrame>
        <div role="status">
          <AuthHeader
            title={
              <span className="inline-flex items-center gap-3">
                <LoaderCircle
                  className="size-6 animate-spin text-ink-faint"
                  aria-hidden="true"
                />
                {t("welcome.signingIn")}
              </span>
            }
          />
        </div>
      </PublicPortalFrame>
    );
  }
  return (
    <PublicPortalFrame>
      <div className="flex flex-col" data-testid="client-portal-link-error">
        {reason === "off" ? (
          <AuthHeader
            title={t("welcome.off")}
            subtitle={t("welcome.offBody")}
          />
        ) : (
          <SignInLinkCard
            title={t(`welcome.${reason}`)}
            subtitle={t("welcome.getNew")}
          />
        )}
      </div>
    </PublicPortalFrame>
  );
};
